import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Drawer,
  Form,
  Input,
  Switch,
  Table,
  Popconfirm,
  message,
  Space,
  TimePicker,
  Row,
  Col,
} from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import BreakTypeService from "../../Services/Manage/BreakTypeService";

function BreakType() {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [breaks, setBreaks] = useState([]);
  const [filteredBreaks, setFilteredBreaks] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    fetchBreaks();
  }, []);

  const fetchBreaks = async () => {
    try {
      const response = await BreakTypeService.getAllBreaks();
      const data = response.data;
      const formattedData = data.map((item) => {
        let duration = null;
        if (item["breaks_in_hour/min"]) {
          const { hours = 0, minutes = 0 } = item["breaks_in_hour/min"];
          duration = moment().hours(hours).minutes(minutes).seconds(0);
        }
        return {
          ...item,
          key: item.id?.toString() || item.key,
          duration,
          seconds: item.breaks_in_second,
          status: item.status,
        };
      });
      setBreaks(formattedData);
      setFilteredBreaks(formattedData);
    } catch (error) {
      console.error("Fetch error:", error);
      message.error("Failed to load breaks");
    }
  };

  const showDrawer = () => {
    setEditingKey(null);
    form.resetFields();
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    form.resetFields();
    setEditingKey(null);
  };

  const onFinish = async (values) => {
    const durationMoment = values.duration;
    if (!durationMoment) {
      message.error("Please select break duration.");
      return;
    }

    const totalSeconds = durationMoment.hour() * 3600 + durationMoment.minute() * 60;
    if (totalSeconds === 0) {
      message.error("Duration cannot be zero.");
      return;
    }

    const omeUser = JSON.parse(localStorage.getItem("ome_user"));

    const payload = {
      title: values.title,
      "breaks_in_hour/min": durationMoment.format("HH:mm"),
      breaks_in_second: totalSeconds,
      status: values.status ? "Active" : "Inactive",
      added_by: omeUser?.added_by || "",
    };

    try {
      if (editingKey) {
        await BreakTypeService.updateBreak(editingKey, payload);
        message.success("Break updated successfully!");
      } else {
        await BreakTypeService.createBreak(payload);
        message.success("Break added successfully!");
      }
      fetchBreaks();
      closeDrawer();
    } catch {
      message.error("Operation failed");
    }
  };

  const handleDelete = async (key) => {
    try {
      await BreakTypeService.deleteBreak(key);
      setBreaks(breaks.filter((item) => item.key !== key));
      setFilteredBreaks(filteredBreaks.filter((item) => item.key !== key));
      message.success("Break deleted successfully!");
    } catch {
      message.error("Delete failed");
    }
  };

  const handleEdit = (record) => {
    setEditingKey(record.key);
    form.setFieldsValue({
      title: record.title,
      duration: record.duration,
      status: record.status === "Active",
    });
    setDrawerVisible(true);
  };


  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);

    const filtered = breaks.filter((item) => {
      const title = item.title?.toLowerCase() || "";
      const duration = item.duration ? item.duration.format("HH:mm").toLowerCase() : "";
      const status = item.status?.toLowerCase() || "";
      return title.includes(value) || duration.includes(value) || status.includes(value);
    });

    setFilteredBreaks(filtered);
  };

const columns = [
  {
    title: "Title",
    dataIndex: "title",
    key: "title",
    align: "center",
  },
  {
    title: "Breaks (hours/min)",
    key: "duration",
    align: "center",
    render: (_, record) => (record.duration ? record.duration.format("HH:mm") : ""),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    align: "center",
    render: (text) => (
      <span style={{ color: text === "Active" ? "green" : "red" }}>{text}</span>
    ),
  },
  {
    title: "Action",
    key: "action",
    align: "center",
    render: (_, record) => (
      <Space size="middle">
        <Button
          type="default"
          size="large"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
        />
        <Popconfirm
          title="Are you sure to delete this break?"
          onConfirm={() => handleDelete(record.key)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="default" size="large" icon={<DeleteOutlined />} danger />
        </Popconfirm>
      </Space>
    ),
  },
];


  return (
    <div>
      <Card
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <h3 style={{ margin: 0 }}>Break Type</h3>
            </Col>
            <Col>
              <Input
                placeholder="Search..."
                value={searchText}
                onChange={handleSearch}
                allowClear
                size="large"
                style={{ width: 280, fontSize: 16 }}
              />
            </Col>
          </Row>
        }
       extra={
  <Button
    type="primary"
    onClick={showDrawer}
    style={{ marginLeft: 16 }}
  >
    Add Break
  </Button>
}

      >
        {filteredBreaks.length === 0 ? (
          <p>No breaks added yet.</p>
        ) : (
      <Table
  dataSource={filteredBreaks}
  columns={columns}
  pagination={false}
  rowKey="key"
  size="small" // reduces the row height (less padding)
  style={{ textAlign: "center" }} // centers the data horizontally
/>

        )}
      </Card>

      <Drawer
        title={editingKey ? "Edit Break" : "Add Break"}
        width={360}
        onClose={closeDrawer}
        visible={drawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please enter the title" }]}
          >
            <Input placeholder="Enter break title" />
          </Form.Item>

          <Form.Item
            name="duration"
            label="Break Duration (HH:mm)"
            rules={[{ required: true, message: "Please select break duration" }]}
          >
            <TimePicker
              format="HH:mm"
              minuteStep={1}
              use12Hours={false}
              placeholder="Select duration"
            />
          </Form.Item>

          <Form.Item name="status" label="Status" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {editingKey ? "Update Break" : "Add Break"}
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
}

export default BreakType;
